import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Receipt, 
  ChefHat, 
  Bell, 
  CheckCircle, 
  XCircle,
  Clock,
  RefreshCw,
  Loader2,
  ShoppingBag,
  BellRing,
  BellOff,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configuration
const STATUS_CONFIG = {
  'Pesanan Diterima': { icon: Receipt, color: 'text-blue-500', bgColor: 'bg-blue-500/10', step: 1 },
  'Sedang Dimasak': { icon: ChefHat, color: 'text-orange-500', bgColor: 'bg-orange-500/10', step: 2 },
  'Siap Diantar': { icon: Bell, color: 'text-green-500', bgColor: 'bg-green-500/10', step: 3 },
  'Selesai': { icon: CheckCircle, color: 'text-emerald-500', bgColor: 'bg-emerald-500/10', step: 4 },
  'Dibatalkan': { icon: XCircle, color: 'text-red-500', bgColor: 'bg-red-500/10', step: -1 },
};

const TIMELINE_STEPS = [
  { step: 1, label: 'Pesanan Diterima', icon: Receipt },
  { step: 2, label: 'Sedang Dimasak', icon: ChefHat },
  { step: 3, label: 'Siap Diantar', icon: Bell },
  { step: 4, label: 'Selesai', icon: CheckCircle },
];

// Check if push notifications are supported
const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

// Convert VAPID key to Uint8Array
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export default function OrderTracking({ orderId, tableId, token, onClose, showNotificationPrompt = true }) {
  const [orderStatus, setOrderStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Check if already subscribed to push
  useEffect(() => {
    const checkPushSubscription = async () => {
      if (!isPushSupported()) return;
      
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          setPushEnabled(true);
        } else if (showNotificationPrompt && Notification.permission === 'default') {
          // Show prompt after a short delay
          setTimeout(() => setShowPushPrompt(true), 2000);
        }
      } catch (err) {
        console.log('Push check error:', err);
      }
    };
    
    checkPushSubscription();
  }, [showNotificationPrompt]);

  // Subscribe to push notifications
  const subscribeToPush = async () => {
    if (!isPushSupported()) {
      console.log('Push notifications not supported');
      return;
    }
    
    setPushLoading(true);
    setShowPushPrompt(false);
    
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        setPushLoading(false);
        return;
      }
      
      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      
      // Get VAPID public key
      const vapidResponse = await fetch(`${API_URL}/api/public/push/vapid-key`);
      if (!vapidResponse.ok) throw new Error('Failed to get VAPID key');
      const { vapid_public_key } = await vapidResponse.json();
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid_public_key)
      });
      
      // Send subscription to backend
      const subscribeResponse = await fetch(`${API_URL}/api/public/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderId,
          table_id: tableId,
          subscription: subscription.toJSON()
        })
      });
      
      if (!subscribeResponse.ok) throw new Error('Failed to subscribe');
      
      setPushEnabled(true);
      console.log('Push notification subscribed');
    } catch (err) {
      console.error('Push subscription error:', err);
    } finally {
      setPushLoading(false);
    }
  };

  // Decline push notifications
  const declinePush = () => {
    setShowPushPrompt(false);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `Rp ${Math.round(amount).toLocaleString('id-ID')}`;
  };

  // Fetch order status via API (polling fallback)
  const fetchOrderStatus = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/public/orders/${orderId}/status?table_id=${tableId}&token=${token}`
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch order status');
      }
      
      const data = await response.json();
      setOrderStatus(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orderId, tableId, token]);

  // Setup WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const wsUrl = `${API_URL.replace('https://', 'wss://').replace('http://', 'ws://')}/api/public/orders/${orderId}/ws?table_id=${tableId}&token=${token}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setWsConnected(true);
        // Stop polling when WS is connected
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.event === 'status_update') {
            setOrderStatus(prev => ({
              ...prev,
              ...message.data
            }));
          }
        } catch (e) {
          console.error('Failed to parse WS message:', e);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setWsConnected(false);
        wsRef.current = null;
        
        // Reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 5000);
        
        // Start polling as fallback
        if (!pollingIntervalRef.current) {
          pollingIntervalRef.current = setInterval(fetchOrderStatus, 10000);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        ws.close();
      };

      // Setup ping interval
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 25000);

      return () => {
        clearInterval(pingInterval);
      };
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
      // Fallback to polling
      if (!pollingIntervalRef.current) {
        pollingIntervalRef.current = setInterval(fetchOrderStatus, 10000);
      }
    }
  }, [orderId, tableId, token, fetchOrderStatus]);

  // Initial fetch and setup
  useEffect(() => {
    fetchOrderStatus();
    connectWebSocket();

    return () => {
      // Cleanup
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [fetchOrderStatus, connectWebSocket]);

  // Get status config
  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG['Pesanan Diterima'];
  };

  if (loading) {
    return (
      <Card className="border-2">
        <CardContent className="p-6 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Memuat status pesanan...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-2 border-destructive/50">
        <CardContent className="p-6 text-center">
          <XCircle className="h-8 w-8 text-destructive mx-auto" />
          <p className="mt-2 text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={fetchOrderStatus}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Coba Lagi
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!orderStatus) return null;

  const statusConfig = getStatusConfig(orderStatus.status);
  const StatusIcon = statusConfig.icon;
  const currentStep = orderStatus.step;
  const isCancelled = currentStep === -1;
  const isCompleted = currentStep === 4;

  return (
    <Card className="border-2 border-primary/20 overflow-hidden">
      {/* Status Header */}
      <div className={`p-4 ${statusConfig.bgColor}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full bg-white/80 ${statusConfig.color}`}>
              <StatusIcon className="h-6 w-6" />
            </div>
            <div>
              <h3 className={`font-bold text-lg ${statusConfig.color}`}>
                {orderStatus.status}
              </h3>
              {!isCancelled && !isCompleted && orderStatus.estimated_minutes > 0 && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Estimasi: {orderStatus.estimated_minutes} menit
                </p>
              )}
            </div>
          </div>
          <Badge variant="outline" className="font-mono">
            #{orderStatus.order_number}
          </Badge>
        </div>
      </div>

      <CardContent className="p-4 space-y-4">
        {/* Timeline */}
        {!isCancelled && (
          <div className="relative">
            <div className="flex justify-between items-center">
              {TIMELINE_STEPS.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = currentStep >= step.step;
                const isCurrent = currentStep === step.step;
                
                return (
                  <div key={step.step} className="flex flex-col items-center z-10">
                    <div 
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isActive 
                          ? isCurrent 
                            ? 'bg-primary text-primary-foreground scale-110 shadow-lg' 
                            : 'bg-primary/80 text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {isActive && !isCurrent ? (
                        <CheckCircle className="h-5 w-5" />
                      ) : (
                        <StepIcon className="h-5 w-5" />
                      )}
                    </div>
                    <span className={`text-xs mt-1 text-center max-w-16 ${
                      isActive ? 'text-foreground font-medium' : 'text-muted-foreground'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
            
            {/* Progress Line */}
            <div className="absolute top-5 left-8 right-8 h-0.5 bg-muted -z-0">
              <div 
                className="h-full bg-primary transition-all duration-500"
                style={{ 
                  width: `${Math.max(0, Math.min(100, ((currentStep - 1) / 3) * 100))}%` 
                }}
              />
            </div>
          </div>
        )}

        {/* Cancelled Message */}
        {isCancelled && (
          <div className="text-center py-4">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
            <p className="text-muted-foreground">Pesanan ini telah dibatalkan</p>
          </div>
        )}

        {/* Completed Message */}
        {isCompleted && (
          <div className="text-center py-4 bg-emerald-500/10 rounded-lg">
            <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-2" />
            <p className="font-medium text-emerald-600">Terima kasih!</p>
            <p className="text-sm text-muted-foreground">Pesanan Anda telah selesai</p>
          </div>
        )}

        {/* Push Notification Prompt */}
        {showPushPrompt && !isCompleted && !isCancelled && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3" data-testid="push-notification-prompt">
            <div className="flex items-start gap-3">
              <BellRing className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-blue-700 dark:text-blue-400">
                  Aktifkan notifikasi?
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Kami akan memberi tahu saat pesanan siap diantar
                </p>
                <div className="flex gap-2 mt-2">
                  <Button 
                    size="sm" 
                    className="h-7 text-xs"
                    onClick={subscribeToPush}
                    disabled={pushLoading}
                    data-testid="enable-push-btn"
                  >
                    {pushLoading ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : (
                      <BellRing className="h-3 w-3 mr-1" />
                    )}
                    Ya, Aktifkan
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="h-7 text-xs"
                    onClick={declinePush}
                  >
                    Tidak
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Push Enabled Status */}
        {pushEnabled && !isCompleted && !isCancelled && (
          <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400 bg-green-500/10 px-3 py-2 rounded-lg">
            <BellRing className="h-4 w-4" />
            <span>Notifikasi aktif - Anda akan diberitahu saat pesanan siap</span>
          </div>
        )}

        {/* Order Summary */}
        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Total</span>
          </div>
          <span className="font-bold font-mono">{formatCurrency(orderStatus.total)}</span>
        </div>

        {/* Connection Status */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500' : 'bg-yellow-500'}`} />
            {wsConnected ? 'Live update aktif' : 'Polling mode'}
          </span>
          <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={fetchOrderStatus}>
            <RefreshCw className="h-3 w-3 mr-1" />
            Refresh
          </Button>
        </div>

        {/* Close / Order Again */}
        {onClose && (
          <Button variant="outline" className="w-full" onClick={onClose}>
            {isCompleted ? 'Pesan Lagi' : 'Lihat Menu'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
